import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import Templates from './Templates';
import { api } from '../services/api';
import { useApp } from '../context/AppContext';
import { Card } from '../components/UI/Card';
import Button from '../components/UI/Button';
import GenieTip from '../components/GenieTip';
import './Studio.css';

// Une page EN LIGNE ne se supprime JAMAIS directement (un concours/événement en cours
// pourrait avoir des participants). On renvoie vers sa stratégie pour la gérer/dépublier.
// Seuls les brouillons (jamais publiés) sont supprimables.
const isLive = (p) => !!p?.published_at;

/**
 * 📐 Studio (Lot B — mode expert) : Modèles + Pages & concours réunis.
 * L'onglet Modèles rend la page Templates existante telle quelle.
 * L'onglet Pages liste les landing pages (le builder complet reste dans
 * Stratégie pour l'instant — il déménagera ici au Lot D).
 */
export default function Studio() {
  const navigate = useNavigate();
  const { addNotification } = useApp();
  const [tab, setTab] = useState('templates');
  const [pages, setPages] = useState(null);
  const [error, setError] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null); // page en attente de confirmation
  const [deleting, setDeleting] = useState(false);

  const loadPages = () => {
    api.getLandingPages()
      .then(r => setPages(r.landing_pages ?? r.pages ?? r ?? []))
      .catch(() => setError(true));
  };
  useEffect(() => { if (tab === 'pages') loadPages(); }, [tab]);

  // Clic sur la poubelle : une page en ligne renvoie vers sa stratégie (jamais supprimée).
  // Un brouillon ouvre la confirmation de suppression.
  const askDelete = (p) => {
    if (isLive(p)) {
      addNotification(`« ${p.title} » est en ligne — gère-la depuis sa stratégie. Dépublie-la d'abord pour pouvoir la supprimer.`, 'error');
      if (p.strategy_instance_id) {
        navigate('/strategy', { state: { focusInstance: String(p.strategy_instance_id) } });
      }
      return;
    }
    setConfirmDel(p);
  };

  const doDelete = async () => {
    if (!confirmDel) return;
    const p = confirmDel;
    setDeleting(true);
    try {
      await api.deleteLandingPage(p.id);
      setPages(list => (list || []).filter(x => x.id !== p.id));
      addNotification(`« ${p.title} » supprimée.`);
      setConfirmDel(null);
    } catch {
      addNotification('Impossible de supprimer cette page. Réessaie.', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="studio-page">
      <div className="studio-tabs">
        <button className={tab === 'templates' ? 'active' : ''} onClick={() => setTab('templates')}>
          ✉️ Modèles
        </button>
        <button className={tab === 'pages' ? 'active' : ''} onClick={() => setTab('pages')}>
          🎯 Pages & concours
        </button>
      </div>

      {tab === 'templates' && <Templates />}

      {tab === 'pages' && (
        <div className="studio-pages">
          <GenieTip
            id="studio-pages"
            title="🎯 Tes pages de capture & concours"
            foot={<>Chaque page a été générée par une <b>stratégie</b>. « Voir la page » ouvre ce que tes clients voient ; « Éditer dans Stratégie » te ramène à la stratégie qui la pilote pour la modifier.</>}
          >
            Ce sont les mini-sites où tes prospects laissent leurs coordonnées (formulaire + consentement + tirage au sort intégrés). <b>Publiée</b> = en ligne, <b>Brouillon</b> = pas encore visible.
          </GenieTip>

          {error && (
            <Card>
              <div className="studio-error">
                Impossible de charger tes pages.
                <button onClick={() => { setError(false); loadPages(); }}>Réessayer</button>
              </div>
            </Card>
          )}

          {pages === null && !error && <Card><p className="studio-loading">Chargement…</p></Card>}

          {Array.isArray(pages) && pages.length === 0 && (
            <Card>
              <div className="studio-empty">
                <span className="studio-empty-icon">🎯</span>
                <h3>Aucune page pour le moment</h3>
                <p>Les pages et concours se créent depuis une stratégie — Appee les génère prêtes à publier, avec formulaire, consentement et tirage au sort intégrés.</p>
                <Button onClick={() => navigate('/strategy')}>Créer depuis Stratégie →</Button>
              </div>
            </Card>
          )}

          {Array.isArray(pages) && pages.length > 0 && (
            <div className="studio-grid">
              {pages.map(p => (
                <Card key={p.id}>
                  <div className="studio-lp">
                    <div className="studio-lp-head">
                      <h3 className="studio-lp-title">{p.title || p.slug}</h3>
                      <span className={`studio-lp-status ${p.published_at ? 'on' : ''}`}>
                        {p.published_at ? '🟢 Publiée' : '⚪ Brouillon'}
                      </span>
                    </div>
                    <div className="studio-lp-stats">
                      <span>👁 {p.visits_count ?? 0} visites</span>
                      <span>📥 {p.leads_count ?? 0} leads</span>
                    </div>
                    <div className="studio-lp-actions">
                      {p.published_at && (
                        <a href={`/lp/${p.slug}`} target="_blank" rel="noreferrer" className="studio-lp-link">
                          Voir la page ↗
                        </a>
                      )}
                      <button
                        className="studio-lp-edit"
                        onClick={() => navigate('/strategy', p.strategy_instance_id ? { state: { focusInstance: String(p.strategy_instance_id) } } : undefined)}
                      >
                        Éditer dans Stratégie →
                      </button>
                      <button
                        className={`studio-lp-del ${isLive(p) ? 'locked' : ''}`}
                        onClick={() => askDelete(p)}
                        title={isLive(p) ? 'En ligne — gère-la depuis sa stratégie (dépublie pour supprimer)' : 'Supprimer cette page'}
                        aria-label={isLive(p) ? 'Page en ligne — gérer dans la stratégie' : 'Supprimer la page'}
                      >
                        {isLive(p) ? '🔒' : '🗑'}
                      </button>
                    </div>
                  </div>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}

      {confirmDel && createPortal(
        <div className="studio-del-overlay" onClick={() => !deleting && setConfirmDel(null)}>
          <div className="studio-del-modal" onClick={e => e.stopPropagation()}>
            <div className="studio-del-icon">🗑</div>
            <h3 className="studio-del-title">Supprimer cette page ?</h3>
            <p className="studio-del-txt">
              « <strong>{confirmDel.title}</strong> » sera <strong>définitivement supprimée</strong>.
              {(confirmDel.leads_count ?? 0) > 0 && (
                <> Les <strong>{confirmDel.leads_count} lead{confirmDel.leads_count > 1 ? 's' : ''}</strong> déjà collecté{confirmDel.leads_count > 1 ? 's' : ''} sur cette page seront perdus.</>
              )}
            </p>
            <div className="studio-del-actions">
              <button className="studio-del-cancel" onClick={() => setConfirmDel(null)} disabled={deleting}>Annuler</button>
              <button className="studio-del-confirm" onClick={doDelete} disabled={deleting}>
                {deleting ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
